import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaDownload, FaTrophy, FaMedal, FaStar, FaAward, FaUsers, FaCalendarAlt } from 'react-icons/fa';
import { HiAcademicCap, HiBriefcase } from 'react-icons/hi';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';
import profileImage from './Himanshu_rawat.png';

// ======= COUNTER ANIMATION HOOK =======
const useCounter = (end, duration = 2000, startCounting) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, startCounting]);
  return count;
};

// ======= SKILLS DATA =======
const skillCategories = [
  {
    title: 'Languages',
    skills: [
      { name: 'JavaScript', icon: '⚡' },
      { name: 'Python', icon: '🐍' },
      { name: 'C++', icon: '⚙️' },
      { name: 'Java', icon: '☕' },
      { name: 'TypeScript', icon: '🔷' },
    ]
  },
  {
    title: 'Frontend',
    skills: [
      { name: 'React.js', icon: '⚛️' },
      { name: 'HTML5/CSS3', icon: '🎨' },
      { name: 'Framer Motion', icon: '🎬' },
      { name: 'Styled Components', icon: '🎨' },
    ]
  },
  {
    title: 'Backend & DB',
    skills: [
      { name: 'Node.js', icon: '🟢' },
      { name: 'Express.js', icon: '🚀' },
      { name: 'MongoDB', icon: '🍃' },
      { name: 'MySQL', icon: '🗄️' },
      { name: 'Redis', icon: '🔴' },
      { name: 'Socket.io', icon: '🔌' },
    ]
  },
  {
    title: 'Cloud & DevOps',
    skills: [
      { name: 'AWS EC2', icon: '☁️' },
      { name: 'AWS Lambda', icon: '⚡' },
      { name: 'S3 / CloudFront', icon: '📦' },
      { name: 'Nginx', icon: '🌐' },
      { name: 'Firebase', icon: '🔥' },
      { name: 'Vercel', icon: '▲' },
    ]
  },
  {
    title: 'Tools',
    skills: [
      { name: 'Git & GitHub', icon: '🔀' },
      { name: 'Postman', icon: '📮' },
      { name: 'Linux/Unix', icon: '🐧' },
      { name: 'Google Play', icon: '📱' },
    ]
  },
];

// ======= EXPERIENCE DATA =======
const experienceData = [
  {
    title: 'Team Lead & Product Owner',
    company: 'QuickRoll — Enterprise Attendance System',
    period: 'March 2025 - Present',
    description: 'Led a team of 3 developers building a MERN stack attendance system deployed on AWS EC2 with Nginx reverse proxy and Redis caching, supporting 7,000+ students. Implemented serverless face verification via AWS Lambda and DeepFace, integrated CloudFront CDN and Amazon SES for reliable OTP delivery.',
    tags: ['MERN', 'AWS', 'Redis', 'Nginx', 'DeepFace'],
    icon: <HiBriefcase />
  },
  {
    title: 'Web Developer',
    company: 'Co-Dev Club — Hack-O-Holic 3.0',
    period: 'March 2025 - April 2025',
    description: 'Designed and developed the complete event management website for the club\'s flagship 24-hour hackathon. Built responsive event pages, registration forms, live updates, and coordinated logistics for 200+ participants.',
    tags: ['React', 'Event Platform', 'Responsive Design'],
    icon: <HiBriefcase />
  },
  {
    title: 'Tech Team Lead',
    company: 'DigiToken — Farewell Entry System',
    period: 'March 2025',
    description: 'Developed a fraud-proof QR-based entry system using AES-256 encryption, preventing ticket duplication for university events. Built real-time admin dashboard with Socket.io tracking live entry counts with <20ms latency.',
    tags: ['MERN', 'AES-256', 'Socket.io', 'QR Codes'],
    icon: <HiBriefcase />
  },
];

// ======= EDUCATION DATA =======
const educationData = [
  {
    degree: 'B.Tech — Computer Science',
    institution: 'Graphic Era Hill University, Dehradun',
    period: '2023 - Present',
    grade: 'CGPA: 8.62/10.0',
    icon: <HiAcademicCap />
  },
  {
    degree: 'Higher Secondary (Class XII)',
    institution: 'Himalayan English School, Uttarakhand Board',
    period: '2023',
    grade: '76.4%',
    icon: <HiAcademicCap />
  },
  {
    degree: 'Matriculation (Class X)',
    institution: 'Himalayan English School',
    period: '2021',
    grade: '91.8%',
    icon: <HiAcademicCap />
  },
];

// ======= ACHIEVEMENTS DATA =======
const achievements = [
  {
    icon: <FaTrophy />,
    title: 'Hackathon Champion',
    desc: '1st Rank in Hack-O-Holic 3.0 (24-Hour Hackathon) — built a fully responsive event platform',
    color: '#facc15',
  },
  {
    icon: <FaMedal />,
    title: 'National Finalist',
    desc: 'Top 5 at TECHSPRINT National Level Hackathon, competing against teams from across the country',
    color: '#f97316',
  },
  {
    icon: <FaStar />,
    title: '750+ DSA Problems',
    desc: 'Top 18% Global on LeetCode with 173-day consistency streak in Data Structures & Algorithms',
    color: '#06b6d4',
  },
  {
    icon: <FaAward />,
    title: 'NPTEL Elite + Gold',
    desc: 'Top 2% nationwide in Object Oriented Programming in C++ certification',
    color: '#a855f7',
  },
];

// ======= COMPONENT =======
const About = () => {
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.5 });

  const projectCount = useCounter(8, 1500, statsInView);
  const dsaCount = useCounter(750, 2000, statsInView);
  const streakCount = useCounter(173, 1800, statsInView);

  return (
    <>
      {/* ========== ABOUT SECTION ========== */}
      <AboutSection id="about">
        <div className="container">
          <SectionTitle subtitle="Get To Know Me" title="About Me" />

          <AboutGrid>
            <AboutImageCol
              as={motion.div}
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <ImageContainer>
                <AboutImg src={profileImage} alt="Himanshu Rawat" />
                <ImageDecoration />
              </ImageContainer>
            </AboutImageCol>

            <AboutInfoCol
              as={motion.div}
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <AboutBio>
                <p>
                  I'm a passionate <Highlight>full stack developer</Highlight> and <Highlight>competitive programmer</Highlight> based
                  in Dehradun, India. My journey in tech began during my 2nd year at university and has quickly evolved
                  into building production-grade applications serving thousands of users.
                </p>
                <p>
                  I specialize in the <Highlight>MERN stack</Highlight> with a strong focus on cloud infrastructure
                  using <Highlight>AWS</Highlight> (EC2, Lambda, S3, CloudFront). I've deployed systems handling
                  7,000+ concurrent users with Nginx load balancing and Redis caching.
                </p>
                <p>
                  Beyond development, I actively sharpen my problem-solving through competitive programming —
                  having solved <Highlight>750+ DSA problems</Highlight> on LeetCode with a <Highlight>173-day streak</Highlight>.
                  When I'm not coding, you'll find me enjoying a game of table tennis.
                </p>
              </AboutBio>

              <StatsRow ref={statsRef}>
                <StatCard>
                  <StatValue>{projectCount}+</StatValue>
                  <StatLabel>Projects Built</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{dsaCount}+</StatValue>
                  <StatLabel>DSA Problems</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{streakCount}</StatValue>
                  <StatLabel>Day Streak</StatLabel>
                </StatCard>
              </StatsRow>

              <Button
                href="https://drive.google.com/file/d/12UGKSdczT0cU5esCnFPjF2tUOlkenmaX/view?usp=sharing"
              >
                Download Resume <FaDownload />
              </Button>
            </AboutInfoCol>
          </AboutGrid>
        </div>
      </AboutSection>

      {/* ========== SKILLS SECTION ========== */}
      <SkillsSection id="skills">
        <div className="container">
          <SectionTitle subtitle="Tech Arsenal" title="Skills & Technologies" />

          <SkillsContainer>
            {skillCategories.map((cat, catIndex) => (
              <SkillCategoryCard
                key={cat.title}
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: catIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <CategoryHeader>{cat.title}</CategoryHeader>
                <SkillsGrid>
                  {cat.skills.map((skill, i) => (
                    <SkillChip key={skill.name}>
                      <SkillIcon>{skill.icon}</SkillIcon>
                      <SkillName>{skill.name}</SkillName>
                    </SkillChip>
                  ))}
                </SkillsGrid>
              </SkillCategoryCard>
            ))}
          </SkillsContainer>
        </div>
      </SkillsSection>

      {/* ========== EXPERIENCE & EDUCATION ========== */}
      <ExperienceSection id="experience">
        <div className="container">
          <SectionTitle subtitle="My Journey" title="Experience & Education" />

          <TimelineGrid>
            <TimelineColumn>
              <ColumnTitle>
                <HiBriefcase /> Work Experience
              </ColumnTitle>
              <TimelineWrapper>
                {experienceData.map((item, index) => (
                  <TimelineCard
                    key={index}
                    as={motion.div}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <TimelineDotGlow />
                    <CardContent>
                      <CardTop>
                        <CardTitle>{item.title}</CardTitle>
                        <CardPeriod><FaCalendarAlt /> {item.period}</CardPeriod>
                      </CardTop>
                      <CardCompany>{item.company}</CardCompany>
                      <CardDesc>{item.description}</CardDesc>
                      <TagRow>
                        {item.tags.map(tag => (
                          <Tag key={tag}>{tag}</Tag>
                        ))}
                      </TagRow>
                    </CardContent>
                  </TimelineCard>
                ))}
              </TimelineWrapper>
            </TimelineColumn>

            <TimelineColumn>
              <ColumnTitle>
                <HiAcademicCap /> Education
              </ColumnTitle>
              <TimelineWrapper>
                {educationData.map((item, index) => (
                  <TimelineCard
                    key={index}
                    as={motion.div}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <TimelineDotGlow />
                    <CardContent>
                      <CardTop>
                        <CardTitle>{item.degree}</CardTitle>
                        <CardPeriod><FaCalendarAlt /> {item.period}</CardPeriod>
                      </CardTop>
                      <CardCompany>{item.institution}</CardCompany>
                      <GradeBadge>{item.grade}</GradeBadge>
                    </CardContent>
                  </TimelineCard>
                ))}
              </TimelineWrapper>

              {/* Achievements inline */}
              <ColumnTitle style={{ marginTop: '3rem' }}>
                <FaTrophy /> Achievements
              </ColumnTitle>
              <AchievementsList>
                {achievements.map((ach, i) => (
                  <AchievementCard
                    key={i}
                    as={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 5 }}
                    $color={ach.color}
                  >
                    <AchIcon $color={ach.color}>{ach.icon}</AchIcon>
                    <AchContent>
                      <AchTitle>{ach.title}</AchTitle>
                      <AchDesc>{ach.desc}</AchDesc>
                    </AchContent>
                  </AchievementCard>
                ))}
              </AchievementsList>
            </TimelineColumn>
          </TimelineGrid>
        </div>
      </ExperienceSection>
    </>
  );
};

// ===== STYLED COMPONENTS  =====

// --- About ---
const AboutSection = styled.section`
  background: ${props => props.theme.colors.backgroundAlt};
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: 2rem;
  }
`;

const AboutImageCol = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
    justify-content: center;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: 380px;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    max-width: 280px;
  }
`;

const AboutImg = styled.img`
  width: 100%;
  border-radius: 16px;
  position: relative;
  z-index: 2;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const ImageDecoration = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: -20px;
  bottom: -20px;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 16px;
  opacity: 0.3;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const AboutInfoCol = styled.div``;

const AboutBio = styled.div`
  margin-bottom: 2rem;
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.8;
    font-size: 1rem;
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Highlight = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: 0.6rem;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 12px;
  padding: 1.2rem;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: 0.8rem 0.5rem;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  background: ${props => props.theme.colors.gradientPrimary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: ${props => props.theme.fonts.mono};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 1.4rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 0.3rem;
`;

// --- Skills ---
const SkillsSection = styled.section``;

const SkillsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const SkillCategoryCard = styled.div`
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
    box-shadow: 0 8px 30px rgba(99, 102, 241, 0.08);
  }
`;

const CategoryHeader = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-family: ${props => props.theme.fonts.mono};
`;

const SkillsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SkillChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  cursor: default;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.06);
    border-color: rgba(99, 102, 241, 0.15);
    transform: translateY(-3px);
  }
`;

const SkillIcon = styled.span`
  font-size: 1.1rem;
`;

const SkillName = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 500;
`;

// --- Experience & Education ---
const ExperienceSection = styled.section`
  background: ${props => props.theme.colors.backgroundAlt};
`;

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TimelineColumn = styled.div``;

const ColumnTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
  }
`;

const TimelineWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-left: 24px;
  
  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 1px;
    background: linear-gradient(
      180deg, 
      ${props => props.theme.colors.primary} 0%, 
      rgba(99, 102, 241, 0.1) 100%
    );
  }
`;

const TimelineCard = styled.div`
  position: relative;
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
    transform: translateX(4px);
  }
`;

const TimelineDotGlow = styled.div`
  position: absolute;
  left: -24px;
  top: 18px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  box-shadow: 0 0 12px ${props => props.theme.colors.glow};
  z-index: 2;
`;

const CardContent = styled.div`
  padding: 1.2rem 1.5rem;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.3rem;
  }
`;

const CardTitle = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const CardPeriod = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.mono};
  display: flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
`;

const CardCompany = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 0.6rem;
`;

const CardDesc = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textMuted};
  line-height: 1.7;
  margin-bottom: 0.8rem;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Tag = styled.span`
  font-size: 0.7rem;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(99, 102, 241, 0.1);
  color: ${props => props.theme.colors.primaryLight};
  font-family: ${props => props.theme.fonts.mono};
  font-weight: 500;
`;

const GradeBadge = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  padding: 4px 12px;
  border-radius: 6px;
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.mono};
`;

// --- Achievements ---
const AchievementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const AchievementCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.2rem;
  background: ${props => props.theme.card.background};
  border: ${props => props.theme.card.border};
  border-left: 3px solid ${props => props.$color};
  border-radius: 10px;
  transition: all 0.3s ease;
  cursor: default;
`;

const AchIcon = styled.div`
  color: ${props => props.$color};
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 2px;
`;

const AchContent = styled.div``;

const AchTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.2rem;
`;

const AchDesc = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textMuted};
  line-height: 1.6;
`;

export default About;
